import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  text?: string;
  /** ms; 0 keeps it until dismissed. */
  duration: number;
}

interface ToastState {
  items: ToastItem[];
  push: (item: Omit<ToastItem, 'id'>) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;
const MAX_VISIBLE = 4;

export const useToastStore = create<ToastState>()((set) => ({
  items: [],
  push: (item) => {
    const id = nextId++;
    set((s) => {
      // Collapse identical consecutive messages (e.g. several failing queries at once).
      const duplicate = s.items.find((t) => t.title === item.title && t.text === item.text);
      const items = duplicate ? s.items.filter((t) => t !== duplicate) : s.items;
      return { items: [...items, { ...item, id }].slice(-MAX_VISIBLE) };
    });
    return id;
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

const show = (tone: ToastTone, defaultDuration: number) => (title: string, text?: string) =>
  useToastStore.getState().push({ tone, title, text, duration: defaultDuration });

/** Imperative notifications, usable outside React (API client, mutations). */
export const toast = {
  success: show('success', 4500),
  info: show('info', 5000),
  warning: show('warning', 6000),
  error: show('error', 6500),
  dismiss: (id: number) => useToastStore.getState().dismiss(id),
};
